import * as React from "react";
import { WORKFORCE_PERSONAS, AgentRoutine, PersonaConfig } from "../../../../../convex/aiWorkforce/config";
import { getPersonaTheme } from "./personaTheme";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Calendar,
  Clock,
  Plus,
  Bot,
  Trash2,
  CheckCircle2,
  Info,
  Sparkles,
  ArrowRight,
  MessageSquare,
  Edit2,
  Check,
  X,
} from "lucide-react";

interface AgentRoutinesModalProps {
  isOpen: boolean;
  onClose: () => void;
  personaKey: string | null;
  onOpenChat?: (personaKey: string) => void;
  initialCreating?: boolean;
}

export function AgentRoutinesModal({
  isOpen,
  onClose,
  personaKey,
  onOpenChat,
  initialCreating = false,
}: AgentRoutinesModalProps) {
  if (!personaKey) return null;
  const persona: PersonaConfig = WORKFORCE_PERSONAS[personaKey] || WORKFORCE_PERSONAS.chief_of_staff;
  const theme = getPersonaTheme(persona.key);

  // Active/Inactive state
  const [routinesState, setRoutinesState] = React.useState<Record<string, boolean>>(() => {
    const saved = localStorage.getItem("mc_agent_routines_state");
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {}
    }
    return {};
  });

  // Schedule overrides for pre-made and custom routines (e.g. { zero_miss_shift_monitor: "Daily at 06:00 AM" })
  const [scheduleOverrides, setScheduleOverrides] = React.useState<Record<string, string>>(() => {
    const saved = localStorage.getItem("mc_routine_schedule_overrides");
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {}
    }
    return {};
  });

  // Custom routines created by the user
  const [customRoutines, setCustomRoutines] = React.useState<AgentRoutine[]>(() => {
    const saved = localStorage.getItem(`mc_custom_routines_${personaKey}`);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {}
    }
    return [];
  });

  // Edit Schedule State
  const [editingRoutineId, setEditingRoutineId] = React.useState<string | null>(null);
  const [editFrequency, setEditFrequency] = React.useState<"daily" | "weekly" | "monthly">("daily");
  const [editDay, setEditDay] = React.useState("Mondays");
  const [editTime, setEditTime] = React.useState("07:00 PM");

  // Creation form state
  const [isCreating, setIsCreating] = React.useState(initialCreating);
  const [routineName, setRoutineName] = React.useState("");
  const [routinePrompt, setRoutinePrompt] = React.useState("");
  const [createFrequency, setCreateFrequency] = React.useState<"daily" | "weekly" | "monthly">("daily");
  const [createDay, setCreateDay] = React.useState("Mondays");
  const [createTime, setCreateTime] = React.useState("07:00 PM");

  React.useEffect(() => {
    if (isOpen && initialCreating) {
      setIsCreating(true);
    }
  }, [isOpen, initialCreating]);

  const turnkeyRoutines: AgentRoutine[] = persona.routines || [];
  const allRoutines = [...turnkeyRoutines, ...customRoutines];

  const handleToggle = (id: string, defaultVal = false) => {
    setRoutinesState((prev) => {
      const isCurrentlyEnabled = prev[id] !== undefined ? prev[id] : defaultVal;
      const next = { ...prev, [id]: !isCurrentlyEnabled };
      localStorage.setItem("mc_agent_routines_state", JSON.stringify(next));
      return next;
    });
  };

  const startEditingSchedule = (routine: AgentRoutine) => {
    setEditingRoutineId(routine.id);
    setEditFrequency(routine.frequency === "weekly" ? "weekly" : routine.frequency === "monthly" ? "monthly" : "daily");
    setEditDay("Mondays");
    setEditTime("07:00 PM");
  };

  const handleSaveScheduleEdit = (routineId: string) => {
    let newScheduleLabel = "";
    if (editFrequency === "daily") {
      newScheduleLabel = `Daily at ${editTime}`;
    } else if (editFrequency === "weekly") {
      newScheduleLabel = `${editDay} at ${editTime}`;
    } else {
      newScheduleLabel = `Monthly on 1st at ${editTime}`;
    }

    const nextOverrides = { ...scheduleOverrides, [routineId]: newScheduleLabel };
    setScheduleOverrides(nextOverrides);
    localStorage.setItem("mc_routine_schedule_overrides", JSON.stringify(nextOverrides));
    setEditingRoutineId(null);
  };

  const handleSaveCustomRoutine = () => {
    if (!routineName.trim() || !routinePrompt.trim()) return;

    let scheduleLabel = "";
    if (createFrequency === "daily") {
      scheduleLabel = `Daily at ${createTime}`;
    } else if (createFrequency === "weekly") {
      scheduleLabel = `${createDay} at ${createTime}`;
    } else {
      scheduleLabel = `Monthly on 1st at ${createTime}`;
    }

    const newRoutine: AgentRoutine = {
      id: `custom_${Date.now()}`,
      name: routineName.trim(),
      description: routinePrompt.trim(),
      frequency: createFrequency,
      scheduleLabel,
      schemaTables: ["businesses", "shiftAssignments"],
      outputDeliverable: "Work Review Draft",
      defaultEnabled: true,
    };

    const updated = [...customRoutines, newRoutine];
    setCustomRoutines(updated);
    localStorage.setItem(`mc_custom_routines_${personaKey}`, JSON.stringify(updated));

    // Auto-enable new routine
    setRoutinesState((prev) => {
      const next = { ...prev, [newRoutine.id]: true };
      localStorage.setItem("mc_agent_routines_state", JSON.stringify(next));
      return next;
    });

    // Reset creation form
    setRoutineName("");
    setRoutinePrompt("");
    setIsCreating(false);
  };

  const handleDeleteCustomRoutine = (id: string) => {
    const updated = customRoutines.filter((r) => r.id !== id);
    setCustomRoutines(updated);
    localStorage.setItem(`mc_custom_routines_${personaKey}`, JSON.stringify(updated));
  };

  const activeCount = allRoutines.filter((r) => (routinesState[r.id] !== undefined ? routinesState[r.id] : (r.defaultEnabled ?? false))).length;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0 overflow-hidden bg-white border border-gray-200 rounded-2xl shadow-xl">
        {/* Modal Header */}
        <DialogHeader className="p-6 border-b border-gray-100 bg-gradient-to-r from-slate-50 to-blue-50/40">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3.5 min-w-0">
              <div
                className={`w-12 h-12 rounded-2xl ${theme.bgLight} border ${theme.border} ${theme.text} flex items-center justify-center flex-shrink-0 shadow-xs`}
              >
                <Bot className="w-6 h-6" />
              </div>
              <div className="min-w-0">
                <DialogTitle className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <span>{persona.name}&apos;s Scheduled Routines</span>
                  <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200 font-semibold">
                    {activeCount} Active
                  </Badge>
                </DialogTitle>
                <DialogDescription className="text-sm text-gray-500 mt-0.5">
                  Configure recurring schedules, execution times, and automated checks for {persona.name}.
                </DialogDescription>
              </div>
            </div>
          </div>
        </DialogHeader>

        {/* Modal Body / Routines List */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4 custom-scrollbar bg-slate-50/40">
          {allRoutines.length === 0 && !isCreating ? (
            <div className="p-8 rounded-2xl bg-white border border-gray-200 text-center space-y-3 shadow-xs">
              <div className="w-12 h-12 mx-auto rounded-full bg-blue-50 text-blue-600 flex items-center justify-center">
                <Calendar className="w-6 h-6" />
              </div>
              <h4 className="text-base font-bold text-gray-900">No scheduled routines configured</h4>
              <p className="text-sm text-gray-500 max-w-md mx-auto leading-relaxed">
                {persona.name} operates on-demand. You can assign recurring automated tasks using the button below.
              </p>
              <button
                onClick={() => setIsCreating(true)}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-semibold shadow-sm transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span>Add Custom Routine for {persona.name}</span>
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {allRoutines.map((routine) => {
                const isEnabled = routinesState[routine.id] !== undefined ? routinesState[routine.id] : (routine.defaultEnabled ?? false);
                const isCustom = routine.id.startsWith("custom_");
                const currentSchedule = scheduleOverrides[routine.id] || routine.scheduleLabel;
                const isEditingThis = editingRoutineId === routine.id;

                return (
                  <div
                    key={routine.id}
                    className={`p-4 rounded-xl border transition-all space-y-3 ${
                      isEnabled
                        ? "bg-white border-blue-200 shadow-xs"
                        : "bg-gray-50/80 border-gray-200 opacity-60"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="font-bold text-gray-900 text-base truncate">
                          {routine.name}
                        </span>
                        {isCustom && (
                          <Badge variant="outline" className="text-2xs bg-purple-50 text-purple-700 border-purple-200">
                            Custom
                          </Badge>
                        )}
                      </div>

                      <div className="flex items-center gap-3 flex-shrink-0">
                        {isCustom && (
                          <button
                            onClick={() => handleDeleteCustomRoutine(routine.id)}
                            className="p-1 rounded-md text-gray-400 hover:text-rose-600 transition-colors"
                            title="Delete Routine"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                        <Switch
                          checked={isEnabled}
                          onCheckedChange={() => handleToggle(routine.id)}
                        />
                      </div>
                    </div>

                    <p className="text-sm text-gray-600 leading-relaxed">
                      {routine.description}
                    </p>

                    {/* Schedule Display or Inline Editor */}
                    {!isEditingThis ? (
                      <div className="flex items-center justify-between pt-2 border-t border-gray-100 text-xs">
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center gap-1.5 text-gray-800 font-semibold bg-gray-50 px-2.5 py-1 rounded-lg border border-gray-200">
                            <Clock className="w-3.5 h-3.5 text-blue-600" />
                            {currentSchedule}
                          </span>
                          <button
                            onClick={() => startEditingSchedule(routine)}
                            className="text-xs text-blue-600 hover:text-blue-800 font-semibold flex items-center gap-1 px-2 py-0.5 rounded hover:bg-blue-50 transition-colors"
                            title="Edit Frequency, Day & Time"
                          >
                            <Edit2 className="w-3 h-3" />
                            <span>Edit Schedule</span>
                          </button>
                        </div>

                        <span className="text-blue-700 font-semibold bg-blue-50 px-2 py-0.5 rounded-md text-2xs truncate max-w-[180px]" title={routine.outputDeliverable}>
                          📄 {routine.outputDeliverable}
                        </span>
                      </div>
                    ) : (
                      /* Inline Schedule Editor */
                      <div className="pt-3 border-t border-blue-100 bg-blue-50/50 p-3 rounded-xl space-y-3 animate-in fade-in">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-bold text-blue-900 flex items-center gap-1.5">
                            <Clock className="w-3.5 h-3.5 text-blue-600" />
                            Customize Schedule for {routine.name}
                          </span>
                          <button
                            onClick={() => setEditingRoutineId(null)}
                            className="text-gray-400 hover:text-gray-600 p-0.5"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                          <div>
                            <label className="text-2xs font-bold uppercase tracking-wider text-gray-500 block mb-1">
                              Frequency
                            </label>
                            <select
                              value={editFrequency}
                              onChange={(e) => setEditFrequency(e.target.value as any)}
                              className="w-full text-xs p-2 rounded-lg bg-white border border-gray-300 text-gray-800 focus:outline-none focus:border-blue-600"
                            >
                              <option value="daily">Daily</option>
                              <option value="weekly">Weekly</option>
                              <option value="monthly">Monthly</option>
                            </select>
                          </div>

                          {editFrequency === "weekly" && (
                            <div>
                              <label className="text-2xs font-bold uppercase tracking-wider text-gray-500 block mb-1">
                                Day of Week
                              </label>
                              <select
                                value={editDay}
                                onChange={(e) => setEditDay(e.target.value)}
                                className="w-full text-xs p-2 rounded-lg bg-white border border-gray-300 text-gray-800 focus:outline-none focus:border-blue-600"
                              >
                                <option value="Mondays">Mondays</option>
                                <option value="Tuesdays">Tuesdays</option>
                                <option value="Wednesdays">Wednesdays</option>
                                <option value="Thursdays">Thursdays</option>
                                <option value="Fridays">Fridays</option>
                                <option value="Saturdays">Saturdays</option>
                                <option value="Sundays">Sundays</option>
                              </select>
                            </div>
                          )}

                          <div>
                            <label className="text-2xs font-bold uppercase tracking-wider text-gray-500 block mb-1">
                              Execution Time
                            </label>
                            <select
                              value={editTime}
                              onChange={(e) => setEditTime(e.target.value)}
                              className="w-full text-xs p-2 rounded-lg bg-white border border-gray-300 text-gray-800 focus:outline-none focus:border-blue-600"
                            >
                              <option value="06:00 AM">06:00 AM</option>
                              <option value="07:00 AM">07:00 AM</option>
                              <option value="08:30 AM">08:30 AM</option>
                              <option value="09:00 AM">09:00 AM</option>
                              <option value="12:00 PM">12:00 PM</option>
                              <option value="02:00 PM">02:00 PM</option>
                              <option value="04:00 PM">04:00 PM</option>
                              <option value="05:00 PM">05:00 PM</option>
                              <option value="07:00 PM">07:00 PM</option>
                              <option value="08:00 PM">08:00 PM</option>
                              <option value="10:00 PM">10:00 PM</option>
                            </select>
                          </div>
                        </div>

                        <div className="flex items-center justify-end gap-2 pt-1">
                          <button
                            onClick={() => setEditingRoutineId(null)}
                            className="px-3 py-1.5 rounded-lg bg-white hover:bg-gray-100 border border-gray-300 text-xs font-semibold text-gray-700"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => handleSaveScheduleEdit(routine.id)}
                            className="px-3.5 py-1.5 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 text-xs font-semibold shadow-sm flex items-center gap-1"
                          >
                            <Check className="w-3.5 h-3.5" />
                            <span>Save Schedule</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Inline Custom Routine Creator Form */}
          {isCreating ? (
            <div className="p-5 rounded-2xl bg-blue-50/60 border border-blue-200 space-y-4 animate-in fade-in">
              <div className="flex items-center justify-between border-b border-blue-200/60 pb-3">
                <span className="text-sm font-bold text-blue-900 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-blue-600" />
                  Create Custom Routine for {persona.name}
                </span>
                <button
                  onClick={() => setIsCreating(false)}
                  className="text-gray-400 hover:text-gray-600 text-xs font-semibold"
                >
                  Cancel
                </button>
              </div>

              {/* Boundary hint */}
              <div className="text-xs text-gray-600 bg-white p-3 rounded-xl border border-blue-100 flex items-start gap-2.5 shadow-2xs">
                <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                <span>
                  <strong>{persona.name} ({persona.title})</strong> can monitor live records, check shifts and leads, and prepare review drafts in your Work tab.
                </span>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1">
                    Routine Name
                  </label>
                  <input
                    type="text"
                    value={routineName}
                    onChange={(e) => setRoutineName(e.target.value)}
                    placeholder="e.g. Friday Late Check-in Summary"
                    className="w-full text-sm p-3 rounded-xl bg-white border border-gray-300 text-gray-900 focus:outline-none focus:border-blue-600 shadow-2xs"
                  />
                </div>

                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1">
                    Instructions / What to Check
                  </label>
                  <textarea
                    value={routinePrompt}
                    onChange={(e) => setRoutinePrompt(e.target.value)}
                    placeholder={`Describe what ${persona.name} should check, audit, or prepare on this recurring schedule...`}
                    rows={2}
                    className="w-full text-sm p-3 rounded-xl bg-white border border-gray-300 text-gray-900 focus:outline-none focus:border-blue-600 resize-none shadow-2xs"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1">
                      Frequency
                    </label>
                    <select
                      value={createFrequency}
                      onChange={(e) => setCreateFrequency(e.target.value as any)}
                      className="w-full text-sm p-2.5 rounded-xl bg-white border border-gray-300 text-gray-800 focus:outline-none focus:border-blue-600"
                    >
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>

                  {createFrequency === "weekly" && (
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1">
                        Day of Week
                      </label>
                      <select
                        value={createDay}
                        onChange={(e) => setCreateDay(e.target.value)}
                        className="w-full text-sm p-2.5 rounded-xl bg-white border border-gray-300 text-gray-800 focus:outline-none focus:border-blue-600"
                      >
                        <option value="Mondays">Mondays</option>
                        <option value="Tuesdays">Tuesdays</option>
                        <option value="Wednesdays">Wednesdays</option>
                        <option value="Thursdays">Thursdays</option>
                        <option value="Fridays">Fridays</option>
                        <option value="Saturdays">Saturdays</option>
                        <option value="Sundays">Sundays</option>
                      </select>
                    </div>
                  )}

                  <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1">
                      Execution Time
                    </label>
                    <select
                      value={createTime}
                      onChange={(e) => setCreateTime(e.target.value)}
                      className="w-full text-sm p-2.5 rounded-xl bg-white border border-gray-300 text-gray-800 focus:outline-none focus:border-blue-600"
                    >
                      <option value="06:00 AM">06:00 AM</option>
                      <option value="07:00 AM">07:00 AM</option>
                      <option value="08:30 AM">08:30 AM</option>
                      <option value="09:00 AM">09:00 AM</option>
                      <option value="12:00 PM">12:00 PM</option>
                      <option value="02:00 PM">02:00 PM</option>
                      <option value="04:00 PM">04:00 PM</option>
                      <option value="05:00 PM">05:00 PM</option>
                      <option value="07:00 PM">07:00 PM</option>
                      <option value="08:00 PM">08:00 PM</option>
                      <option value="10:00 PM">10:00 PM</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2.5 pt-2">
                <button
                  onClick={() => setIsCreating(false)}
                  className="px-4 py-2 rounded-xl bg-white hover:bg-gray-100 border border-gray-300 text-sm font-semibold text-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveCustomRoutine}
                  disabled={!routineName.trim() || !routinePrompt.trim()}
                  className="px-4 py-2 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 text-sm font-semibold shadow-sm"
                >
                  Save & Activate Routine
                </button>
              </div>
            </div>
          ) : (
            allRoutines.length > 0 && (
              <button
                onClick={() => setIsCreating(true)}
                className="w-full py-3 px-4 rounded-xl border border-dashed border-gray-300 hover:border-blue-500 hover:bg-blue-50/50 text-sm font-semibold text-gray-600 hover:text-blue-700 flex items-center justify-center gap-2 transition-all shadow-2xs"
              >
                <Plus className="w-4 h-4" />
                <span>Add Custom Routine for {persona.name}</span>
              </button>
            )
          )}
        </div>

        {/* Modal Footer */}
        <DialogFooter className="p-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between gap-3 sm:justify-between">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-white hover:bg-gray-100 border border-gray-300 text-sm font-semibold text-gray-700 transition-colors"
          >
            Close
          </button>

          {onOpenChat && (
            <button
              onClick={() => {
                onClose();
                onOpenChat(persona.key);
              }}
              className="px-5 py-2.5 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-semibold shadow-sm transition-colors flex items-center gap-2"
            >
              <MessageSquare className="w-4 h-4" />
              <span>Chat with {persona.name}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
